import { err, ok, type Result } from "@domain/Result";
import type { ILabels } from "@ports/labels/ILabels";
import type { IPdfOpener } from "@ports/labels/IPdfOpener";

export interface LabelsUseCasesDeps {
	labelsApi: ILabels;
	pdfOpener: IPdfOpener;
}

type OpenLabelPdfError = { kind: "fetch_or_open_failed"; message: string };

/**
 * Drives the box-row "open label" workflow. Fetches the per-box label
 * PDF, hands it to the platform's PDF opener (browser tab in the GUI;
 * CLI viewer otherwise), and surfaces fetch / open failures as a typed
 * Result so the calling vm can choose how to notify the user (toast vs
 * silent vs alternative path) without parsing error messages itself.
 */
export interface LabelsUseCases {
	openLabelPdf(lab: string, robot: string, tracking: string): Promise<Result<void, OpenLabelPdfError>>;
}

async function openLabelPdf(
	deps: LabelsUseCasesDeps,
	lab: string,
	robot: string,
	tracking: string,
): Promise<Result<void, OpenLabelPdfError>> {
	try {
		const blob = await deps.labelsApi.getPdfBlob(lab, robot, tracking);
		await deps.pdfOpener.open(blob);
		return ok(undefined);
	} catch (e: unknown) {
		const message = e instanceof Error ? e.message : "Failed to open label";
		return err({ kind: "fetch_or_open_failed", message });
	}
}

export function makeLabelsUseCases(deps: LabelsUseCasesDeps): LabelsUseCases {
	return {
		openLabelPdf: (lab, robot, tracking) => openLabelPdf(deps, lab, robot, tracking),
	};
}
